import { nanoid } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import type { DefaultAxesParam, RequestedConfig } from '@/widgets/chartsView';
import { getDataRequestedConfig, ChartType } from '@/widgets/chartsView';
import type {
  Filters,
  NumberFilterParams,
  StringFilterParams,
} from '@/features/filters';
import { prepareFilters, getApplicableFilters } from '@/features/filters';
import { DimensionColumnDataType } from '@/entities/columnsConfig';
import { prepareDateRangeValueObject } from '@/shared/api';
import { parseMediaColumnValue } from '@/shared/data';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale } from '@/shared/types';
import * as CalendarChartAPI from '../api/calendarChartAPI';
import type {
  CalendarChartState,
  CalendarDataResponse,
  ChartMeta,
} from './types';

type CalendarChartRequestedConfig = RequestedConfig<CalendarChartState>;

interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CalendarChartState | null;
  chartData:
    | {
        status: DataStatus.idle | DataStatus.error;
        data: null;
      }
    | {
        status: DataStatus.finished;
        requestedConfig: CalendarChartRequestedConfig;
        data: CalendarDataResponse;
      }
    | {
        status: DataStatus.loading;
        requestedConfig: CalendarChartRequestedConfig;
        data: null;
      };
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  chart: null,
  chartData: {
    status: DataStatus.idle,
    data: null,
  },
};

export const calendarChartSlice = createAppSlice({
  name: 'calendarChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CalendarChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.calendarChart.meta.status) ||
              isFinished(state.calendarChart.meta.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.cohortDateRange = action.payload;
      },
    ),
    updateRevenueDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.revenueDateScale = action.payload;
    }),
    updateRevenueDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.revenueDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitDimension = action.payload;
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    getChartData: create.asyncThunk(
      (
        chart: CalendarChartState,
        { getState },
      ): Promise<CalendarDataResponse> => {
        const state = getState() as RootState;

        if (!isFinished(state.calendarChart.meta.status)) {
          return Promise.reject(new Error('Meta not initialized'));
        }

        const { meta } = state.calendarChart;
        const { dimensionsFilters, metricsFilters } = prepareFilters(
          chart.filters,
          meta.data!.metrics,
          meta.data!.dimensions,
        );

        return CalendarChartAPI.getCalendarData({
          split: chart.splitDimension,
          metrics: [chart.primaryMetric, chart.secondaryMetric].filter(Boolean),
          cohortDateScale: chart.cohortDateScale,
          cohortDateRange: prepareDateRangeValueObject(chart.cohortDateRange),
          revenueDateScale: chart.revenueDateScale,
          revenueDateRange: prepareDateRangeValueObject(chart.revenueDateRange),
          probabilisticAttribution: chart.probabilisticAttribution,
          dimensionsFilters,
          metricsFilters,
        });
      },
      {
        pending: (state, action) => {
          state.chartData = {
            status: DataStatus.loading,
            requestedConfig: getDataRequestedConfig(
              action.meta.arg,
            ) as CalendarChartRequestedConfig,
            data: null,
          };
        },
        fulfilled: (state, action) => {
          if (
            (isRequestedConfigExist(state.chartData) &&
              !isEqual(
                state.chartData.requestedConfig,
                getDataRequestedConfig(action.meta.arg),
              )) ||
            !state.chart ||
            !isMetaFinished(state.meta)
          ) {
            return;
          }

          const previousSplitDimension = isRequestedConfigExist(state.chartData)
            ? state.chartData.requestedConfig.splitDimension
            : null;

          state.chartData = {
            requestedConfig: isRequestedConfigExist(state.chartData)
              ? state.chartData.requestedConfig
              : (getDataRequestedConfig(
                  action.meta.arg,
                ) as CalendarChartRequestedConfig),
            status: DataStatus.finished,
            data: action.payload,
          };
          const { splitDimension } = action.meta.arg;
          const optionsToExclude = (
            state.chartData.data.splitOptions[splitDimension] || []
          ).slice(3);
          const isMediaContent = selectSplitDimensionIsMedia({
            calendarChart: state,
          });

          if (previousSplitDimension !== splitDimension) {
            state.chart.excludedSplitOptions = isMediaContent
              ? optionsToExclude.map((option) => {
                  return parseMediaColumnValue(option).value;
                })
              : optionsToExclude;
          }
        },
        rejected: (state, action) => {
          if (
            isRequestedConfigExist(state.chartData) &&
            !isEqual(
              state.chartData.requestedConfig,
              getDataRequestedConfig(action.meta.arg),
            )
          ) {
            return;
          }

          state.chartData.status = DataStatus.error;
        },
      },
    ),
    updateChart: create.reducer<CalendarChartState>((state, action) => {
      state.chart = action.payload;
    }),
    resetChart: create.reducer((state) => {
      if (!isMetaFinished(state.meta)) {
        state.chart = null;

        return;
      }

      state.chart = {
        id: nanoid(),
        ...getDefaultChartProps(state.meta.data.defaultAxis!),
      };
    }),
    resetChartData: create.reducer((state) => {
      state.chartData = {
        status: DataStatus.idle,
        data: null,
      };
    }),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
    toggleExcludedSplitOption: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      const optionIndex =
        state.chart?.excludedSplitOptions.indexOf(action.payload) ?? -1;

      if (optionIndex !== -1) {
        state.chart.excludedSplitOptions =
          state.chart.excludedSplitOptions.toSpliced(optionIndex, 1);
      } else {
        state.chart.excludedSplitOptions.push(action.payload);
      }
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectChartData: (state) => state.chartData,
    selectSplitDimensionIsMedia: (state) => {
      if (!state.meta.data || !state.chart) {
        return false;
      }

      return (
        state.meta.data.config[state.chart.splitDimension]?.type ===
        DimensionColumnDataType.MEDIA
      );
    },
    selectDimensionIsDate: (state) => {
      if (!state.meta.data || !state.chart) {
        return false;
      }

      return (
        state.meta.data.config[state.chart.splitDimension]?.type ===
        DimensionColumnDataType.DATE
      );
    },
  },
});

const getDefaultChartProps = (
  defaultAxes: DefaultAxesParam,
): Omit<CalendarChartState, 'id'> => {
  return {
    type: ChartType.calendar,
    excludedSplitOptions: [],
    syncedYAxes: true,
    filters: [],
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    revenueDateScale: DateScale.DAY,
    revenueDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    probabilisticAttribution: true,
    splitDimension: defaultAxes.dimension,
    primaryMetric: defaultAxes.primaryMetric,
    secondaryMetric: defaultAxes.secondaryMetric,
  };
};

export function isRequestedConfigExist(
  chartsData: State['chartData'],
): chartsData is
  | {
      status: DataStatus.loading;
      requestedConfig: CalendarChartRequestedConfig;
      data: null;
    }
  | {
      status: DataStatus.finished;
      requestedConfig: CalendarChartRequestedConfig;
      data: CalendarDataResponse;
    } {
  return (
    chartsData.status === DataStatus.loading ||
    chartsData.status === DataStatus.finished
  );
}

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

interface GetNumberFilterParams {
  filterName: string;
  selectedDimensions: string[];
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  cohortDateRange: SerializedDateRangeValueObject;
  cohortDateScale: DateScale;
  revenueDateRange: SerializedDateRangeValueObject;
  revenueDateScale: DateScale;
  probabilisticAttribution: boolean;
}

export const getNumberFilterParams =
  (params: GetNumberFilterParams): AppThunk<Promise<NumberFilterParams>> =>
  () => {
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CalendarChartAPI.getNumberFilterParams({
      filterName: params.filterName,
      dimensionsFilters,
      metricsFilters,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      cohortDateScale: params.cohortDateScale,
      revenueDateRange: prepareDateRangeValueObject(params.revenueDateRange),
      revenueDateScale: params.revenueDateScale,
      selectedDimensions: params.selectedDimensions,
      probabilisticAttribution: params.probabilisticAttribution,
    });
  };

interface GetStringFilterParams {
  filterName: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  cohortDateRange: SerializedDateRangeValueObject;
  revenueDateRange: SerializedDateRangeValueObject;
}

export const getStringFilterParams =
  (params: GetStringFilterParams): AppThunk<Promise<StringFilterParams>> =>
  () => {
    const { dimensionsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CalendarChartAPI.getStringFilterParams({
      filterName: params.filterName,
      dimensionsFilters,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      revenueDateRange: prepareDateRangeValueObject(params.revenueDateRange),
    });
  };

export const {
  initMeta,
  updateCohortDateScale,
  updateCohortDateRange,
  updateRevenueDateScale,
  updateRevenueDateRange,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateFilters,
  getChartData,
  updateChart,
  resetChart,
  resetChartData,
  reset,
  toggleExcludedSplitOption,
} = calendarChartSlice.actions;
export const {
  selectChart,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectChartData,
  selectSplitDimensionIsMedia,
  selectDimensionIsDate,
} = calendarChartSlice.selectors;
